<script>
import { getToken } from "@/utils/auth"

const BUSINESS_INFO_LABELS = Object.freeze({
    account_name: "账户名称",
    accountName: "账户名称",
    company_name: "公司名称",
    companyName: "公司名称",
    bank_account: "银行账号",
    bankAccount: "银行账号",
    bank_no: "银行账号",
    bankNo: "银行账号",
    bank: "银行名称",
    bank_name: "银行名称",
    bankName: "银行名称",
    open_bank: "开户行",
    openBank: "开户行",
    opening_bank: "开户行",
    bank_branch: "开户行"
})

export default {
    name: "SettlementTypePanel",
    props: {
        value: {
            type: [String, Number],
            default: ""
        },
        options: {
            type: Array,
            default: () => []
        },
        selectable: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        fallbackOption: {
            type: Object,
            default: null
        },
        fallbackName: {
            type: String,
            default: ""
        },
        proof: {
            type: [String, Array],
            default: ""
        },
        allowProofUpload: {
            type: Boolean,
            default: false
        },
        uploadTitle: {
            type: String,
            default: "上传支付凭证"
        },
        title: {
            type: String,
            default: "提交结算方式"
        },
        displayMode: {
            type: String,
            default: "checkout",
            validator: (value) => ["checkout", "detail"].includes(value)
        }
    },
    data() {
        return {
            uploading: false,
            uploadProgress: 0
        }
    },
    computed: {
        selectedOption() {
            const current = this.options.find((item) => this.normalizeId(item.id) === this.normalizeId(this.value))
            if (current) return current
            if (this.fallbackOption) return this.fallbackOption
            if (this.fallbackName) {
                return {
                    id: this.value,
                    name: this.fallbackName
                }
            }
            return null
        },
        showEmptyState() {
            return this.selectable && !this.loading && this.options.length === 0
        },
        businessInfoItems() {
            if (!this.selectedOption || !this.selectedOption.json_data) return []
            let info = this.selectedOption.json_data
            if (typeof info === "string") {
                try {
                    info = JSON.parse(info)
                } catch (error) {
                    return [{ label: "", value: info }]
                }
            }
            if (!info || typeof info !== "object") return []
            if (Array.isArray(info)) {
                return info.map((value) => ({ label: "", value }))
            }
            return Object.keys(info).map((label) => ({
                label: this.formatBusinessInfoLabel(label),
                value: info[label]
            }))
        },
        businessInfoText() {
            return this.businessInfoItems.map((item) => `${item.label ? item.label + "：" : ""}${item.value}`).join("\n")
        },
        payQrUrl() {
            if (!this.selectedOption || !this.selectedOption.pay_qr_code) return ""
            return this.$util.img(this.selectedOption.pay_qr_code)
        },
        proofList() {
            let list = this.proof
            if (!list) return []
            if (!Array.isArray(list)) list = String(list).split(",")
            return list.filter(Boolean).map((item) => this.$util.img(item))
        },
        uploadAction() {
            return `${String(this.$config.baseUrl || "").replace(/\/$/, "")}/api/upload/headimg`
        },
        uploadData() {
            return {
                app_type: "pc",
                app_type_name: "PC",
                token: getToken()
            }
        },
        uploadButtonText() {
            if (this.uploading) return `上传中 ${this.uploadProgress}%`
            return this.proofList.length ? "重新上传" : "选择图片"
        }
    },
    methods: {
        normalizeId(value) {
            return value === undefined || value === null ? "" : String(value)
        },
        formatBusinessInfoLabel(label) {
            if (!label) return ""
            return BUSINESS_INFO_LABELS[label] || String(label).replace(/_/g, " ")
        },
        handleChange(id) {
            const selected = this.options.find((item) => this.normalizeId(item.id) === this.normalizeId(id)) || null
            this.$emit("input", id)
            this.$emit("change", selected)
        },
        copyBusinessInfo() {
            if (this.businessInfoText) this.$util.copy(this.businessInfoText)
        },
        beforeUpload(file) {
            const isImage = file.type && file.type.indexOf("image/") === 0
            const isLt5M = file.size / 1024 / 1024 < 5
            if (!isImage) this.$message.error("支付凭证只能上传图片")
            if (!isLt5M) this.$message.error("支付凭证图片大小不能超过 5MB")
            if (isImage && isLt5M) {
                this.uploading = true
                this.uploadProgress = 0
            }
            return isImage && isLt5M
        },
        handleUploadProgress(event) {
            this.uploading = true
            this.uploadProgress = Math.round(event.percent || 0)
        },
        handleUploadSuccess(response) {
            this.uploading = false
            this.uploadProgress = 100
            if (response && response.code >= 0 && response.data && response.data.pic_path) {
                this.$emit("proof-uploaded", response.data.pic_path)
                return
            }
            this.$message.error((response && (response.message || response.msg)) || "支付凭证上传失败")
        },
        handleUploadError() {
            this.uploading = false
            this.uploadProgress = 0
            this.$message.error("支付凭证上传失败，请稍后重试")
        }
    }
}
</script>

<template>
    <section v-if="loading || selectedOption || showEmptyState" class="settlement-panel" :class="{ 'settlement-panel--detail': displayMode === 'detail' }">
        <div class="settlement-header">
            <div class="settlement-title">{{ title }}</div>
            <el-tag v-if="selectedOption && !selectable" size="mini">{{ selectedOption.name || fallbackName }}</el-tag>
        </div>

        <div v-if="loading" class="settlement-state">
            <i class="el-icon-loading"></i>
            正在加载结算方式...
        </div>
        <div v-else-if="showEmptyState" class="settlement-state settlement-state-error">
            未找到结算方式，请联系管理员
        </div>
        <template v-else-if="selectedOption">
            <el-radio-group v-if="selectable && options.length > 1" :value="value" class="settlement-options" @input="handleChange">
                <el-radio v-for="item in options" :key="item.id" :label="item.id">{{ item.name }}</el-radio>
            </el-radio-group>
            <div v-else-if="selectable" class="settlement-selected">
                当前结算方式：<strong>{{ selectedOption.name }}</strong>
            </div>

            <div v-if="businessInfoItems.length" class="business-info">
                <div class="business-info-header">
                    <div class="business-info-title">转账账户信息</div>
                    <el-button type="text" icon="el-icon-document-copy" @click="copyBusinessInfo">复制全部</el-button>
                </div>
                <div class="business-info-list">
                    <div v-for="(item, index) in businessInfoItems" :key="index" class="business-info-item">
                        <span v-if="item.label" class="business-info-label">{{ item.label }}</span>
                        <span class="business-info-value">{{ item.value }}</span>
                    </div>
                </div>
            </div>

            <div v-if="payQrUrl || proofList.length || allowProofUpload" class="settlement-media">
                <div v-if="payQrUrl" class="settlement-media-item">
                    <div class="settlement-media-header">
                        <span v-if="allowProofUpload" class="settlement-step">1</span>
                        <div>
                            <div class="settlement-media-title">扫码支付</div>
                            <div class="settlement-media-desc">请使用手机扫描二维码完成付款</div>
                        </div>
                    </div>
                    <div class="qr-code-frame">
                        <el-image class="qr-code-image" :src="payQrUrl" :preview-src-list="[payQrUrl]" fit="contain" alt="支付二维码"></el-image>
                    </div>
                    <div class="settlement-media-hint"><i class="el-icon-view"></i> 点击二维码查看大图</div>
                </div>
                <div v-if="proofList.length && !allowProofUpload" class="settlement-media-item">
                    <div class="settlement-media-header">
                        <div>
                            <div class="settlement-media-title">支付凭证</div>
                            <div class="settlement-media-desc">已上传的付款凭证</div>
                        </div>
                    </div>
                    <div class="proof-list" :class="{ 'is-multiple': proofList.length > 1 }">
                        <el-image v-for="(image, index) in proofList" :key="image + index" class="proof-image" :src="image" :preview-src-list="proofList" fit="contain" alt="支付凭证"></el-image>
                    </div>
                    <div class="settlement-media-hint"><i class="el-icon-view"></i> 点击凭证查看大图</div>
                </div>
                <div v-if="allowProofUpload" class="settlement-media-item settlement-upload" :class="{ 'has-proof': proofList.length }">
                    <div class="settlement-media-header">
                        <span v-if="payQrUrl" class="settlement-step">2</span>
                        <div>
                            <div class="settlement-media-title">{{ uploadTitle }}</div>
                            <div class="settlement-media-desc">付款完成后上传凭证，便于订单审核</div>
                        </div>
                    </div>
                    <div v-if="proofList.length" class="proof-list proof-list-uploaded" :class="{ 'is-multiple': proofList.length > 1 }">
                        <el-image v-for="(image, index) in proofList" :key="image + index" class="proof-image" :src="image" :preview-src-list="proofList" fit="contain" alt="已上传支付凭证"></el-image>
                    </div>
                    <el-upload
                        class="proof-uploader"
                        :action="uploadAction"
                        :data="uploadData"
                        :disabled="uploading"
                        :show-file-list="false"
                        :before-upload="beforeUpload"
                        :on-progress="handleUploadProgress"
                        :on-success="handleUploadSuccess"
                        :on-error="handleUploadError"
                        accept="image/*"
                    >
                        <div v-if="proofList.length" class="proof-reupload" :class="{ 'is-uploading': uploading }">
                            <i :class="uploading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
                            <span aria-live="polite">{{ uploadButtonText }}</span>
                        </div>
                        <div v-else class="proof-upload-placeholder" :class="{ 'is-uploading': uploading }">
                            <i :class="uploading ? 'el-icon-loading' : 'el-icon-upload2'" class="proof-upload-icon"></i>
                            <div class="proof-upload-title" aria-live="polite">{{ uploadButtonText }}</div>
                            <div class="upload-tip">点击此区域选择 JPG、PNG 图片，大小不超过 5MB</div>
                            <el-progress v-if="uploading" class="upload-progress" :percentage="uploadProgress" :stroke-width="4"></el-progress>
                        </div>
                    </el-upload>
                    <div v-if="proofList.length" class="upload-tip upload-tip-after-proof">支持 JPG、PNG 图片，大小不超过 5MB</div>
                </div>
            </div>
        </template>
    </section>
</template>

<style lang="scss" scoped>
.settlement-panel {
    padding: 20px;
    margin-bottom: 10px;
    background-color: #ffffff;
}

.settlement-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.settlement-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.settlement-state {
    padding: 18px 0;
    color: #909399;
    text-align: center;
}

.settlement-state-error {
    color: #f56c6c;
}

.settlement-options {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
}

.settlement-selected {
    color: #606266;
}

.business-info {
    padding: 16px 20px;
    margin-top: 16px;
    background-color: #f7f9fc;
    border: 1px solid #e7ebf2;
    border-radius: 4px;
}

.business-info-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.business-info-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.business-info-header .el-button {
    padding: 4px 0;
}

.business-info-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 32px;
}

.business-info-item {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    line-height: 22px;
    color: #606266;
}

.business-info-label {
    flex: 0 0 72px;
    color: #909399;
}

.business-info-value {
    min-width: 0;
    color: #303133;
    word-break: break-word;
    font-variant-numeric: tabular-nums;
}

.settlement-media-title {
    font-weight: 600;
    color: #303133;
}

.settlement-media {
    display: grid;
    grid-template-columns: repeat(2, minmax(280px, 360px));
    gap: 16px;
    max-width: 736px;
    margin-top: 20px;
}

.settlement-media-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 300px;
    padding: 18px;
    box-sizing: border-box;
    background-color: #fbfcfe;
    border: 1px solid #e4e9f0;
    border-radius: 6px;
}

.settlement-media-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.settlement-step {
    display: inline-flex;
    flex: 0 0 26px;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    font-weight: 600;
    line-height: 26px;
    color: $base-color;
    background-color: rgba($base-color, 0.1);
    border-radius: 50%;
}

.settlement-media-title {
    line-height: 22px;
}

.settlement-media-desc {
    margin-top: 2px;
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.qr-code-frame {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-height: 196px;
    margin-top: 12px;
    background-color: #ffffff;
    border: 1px solid #edf0f5;
    border-radius: 6px;
}

.qr-code-image {
    width: 180px;
    height: 180px;
    cursor: zoom-in;
}

.settlement-media-hint {
    margin-top: 10px;
    font-size: 12px;
    line-height: 18px;
    color: #909399;
    text-align: center;
}

.settlement-media-hint i {
    margin-right: 4px;
}

.proof-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;
}

.proof-list-uploaded {
    flex: 1;
    align-items: center;
    justify-content: center;
}

.proof-image {
    width: 144px;
    height: 144px;
    cursor: zoom-in;
    background-color: #ffffff;
    border: 1px solid #e4e9f0;
    border-radius: 6px;
}

.settlement-upload {
    align-items: stretch;
}

.proof-uploader {
    display: block;
    width: 100%;
    margin-top: 16px;
}

::v-deep .proof-uploader .el-upload {
    display: block;
    width: 100%;
}

::v-deep .proof-uploader .el-upload__input {
    display: none !important;
}

.proof-upload-placeholder {
    display: flex;
    min-height: 184px;
    padding: 20px;
    box-sizing: border-box;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: #ffffff;
    border: 1px dashed #cfd6e0;
    border-radius: 6px;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.proof-upload-placeholder:hover,
.proof-upload-placeholder:focus-within {
    background-color: rgba($base-color, 0.03);
    border-color: $base-color;
}

.proof-upload-placeholder.is-uploading {
    cursor: wait;
}

.proof-upload-icon {
    margin-bottom: 10px;
    font-size: 32px;
    color: $base-color;
}

.proof-upload-title {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    color: #303133;
}

.proof-reupload {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    box-sizing: border-box;
    color: $base-color;
    cursor: pointer;
    background-color: rgba($base-color, 0.04);
    border: 1px dashed rgba($base-color, 0.45);
    border-radius: 4px;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.proof-reupload:hover {
    background-color: rgba($base-color, 0.08);
    border-color: $base-color;
}

.proof-reupload.is-uploading {
    cursor: wait;
}

.proof-reupload i {
    margin-right: 6px;
}

.upload-tip {
    margin-top: 8px;
    font-size: 12px;
    line-height: 18px;
    color: #909399;
    text-align: center;
}

.upload-progress {
    width: 80%;
    margin-top: 12px;
}

.upload-tip-after-proof {
    margin-top: 8px;
}

.settlement-panel.settlement-panel--detail {
    padding: 20px 0 16px;
    margin-bottom: 0;
}

.settlement-panel--detail .settlement-header {
    margin-bottom: 12px;
}

.settlement-panel--detail .settlement-media {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    max-width: none;
    margin-top: 16px;
}

.settlement-panel--detail .settlement-media-item {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 132px minmax(0, 1fr);
    column-gap: 16px;
    min-height: 160px;
    padding: 14px;
    background-color: #fafbfc;
}

.settlement-panel--detail .settlement-media-item:only-child {
    grid-column: 1 / -1;
}

.settlement-panel--detail .settlement-media-header {
    grid-row: 1;
    grid-column: 2;
}

.settlement-panel--detail .settlement-step {
    display: none;
}

.settlement-panel--detail .qr-code-frame {
    grid-row: 1 / span 2;
    grid-column: 1;
    width: 132px;
    height: 132px;
    min-height: 132px;
    margin-top: 0;
}

.settlement-panel--detail .qr-code-image {
    width: 122px;
    height: 122px;
}

.settlement-panel--detail .settlement-media-hint {
    grid-row: 2;
    grid-column: 2;
    align-self: end;
    margin-top: 8px;
    text-align: left;
}

.settlement-panel--detail .proof-list {
    display: grid;
    grid-row: 1 / span 2;
    grid-column: 1;
    grid-template-columns: 132px;
    gap: 6px;
    align-content: center;
    margin-top: 0;
}

.settlement-panel--detail .proof-list.is-multiple {
    grid-template-columns: repeat(2, 63px);
}

.settlement-panel--detail .proof-image {
    width: 130px;
    height: 130px;
}

.settlement-panel--detail .proof-list.is-multiple .proof-image {
    width: 61px;
    height: 61px;
}

.settlement-panel--detail .proof-uploader {
    grid-row: 1 / span 2;
    grid-column: 1;
    margin-top: 0;
}

.settlement-panel--detail .proof-upload-placeholder {
    min-height: 132px;
    padding: 12px;
}

.settlement-panel--detail .proof-upload-placeholder .upload-tip {
    display: none;
}

.settlement-panel--detail .settlement-upload.has-proof .proof-uploader {
    grid-row: 2;
    grid-column: 2;
    align-self: end;
    margin-top: 10px;
}

.settlement-panel--detail .settlement-upload.has-proof .upload-tip-after-proof {
    display: none;
}

@media screen and (max-width: 900px) {
    .business-info-list {
        grid-template-columns: minmax(0, 1fr);
    }

    .settlement-media {
        grid-template-columns: minmax(0, 1fr);
        max-width: 360px;
    }

    .settlement-panel--detail .settlement-media {
        grid-template-columns: minmax(0, 1fr);
        max-width: none;
    }
}
</style>
